// src/components/patient/booking/ServiceBookingPanel.jsx
//
// Steps 2-7 of /patient/book-appointment for ONE live service version:
//
//   review -> choose mode -> choose location (In-Clinic only) -> dates ->
//   select a time (instant, no network) -> Continue into the existing
//   hold/book/payment boundary (HoldAndBook).
//
// Dependency resets, stated once:
//   * mode change      clears the location when it no longer applies, and the slot;
//   * location change  clears the slot;
//   * date change      clears the slot.
// A new service remounts this panel (keyed by version id), clearing everything.
//
// Slot loading is sequenced and cancellable, and never disables the mode,
// location or date controls.
//
// Continue issues the ONE canonical hold request immediately (holdSession's
// beginHold) and hands that in-flight operation to HoldAndBook, which consumes
// it instead of starting its own hold when it mounts. A second click while the
// operation is pending is ignored. While a hold is live the selection is
// locked; closing, pressing Escape or choosing another time opens an
// application dialog and releases the hold. A hold request abandoned before it
// answers is released as soon as it lands. An existing hold (e.g. after a
// refresh) is offered for recovery (secured POST) or release.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { apiErrorCode, readApiError } from "../../../lib/apiErrors";
import {
  dateRangeProblem,
  formatLocalDate,
  formatTimeInZone,
} from "../../../lib/displayTimezone";
import { beginHold, beginRecovery } from "../../../lib/holdSession";
import useSequencedRequest from "../../../lib/useSequencedRequest";
import { patientBookingApi } from "../../../services/patientBooking";
import { patientDiscoveryApi } from "../../../services/patientDiscovery";

import ConfirmDialog from "./ConfirmDialog";
import DateOfBirthDialog from "./DateOfBirthDialog";
import HoldAndBook, { holdRequestFor } from "./HoldAndBook";
import {
  Icon,
  InlineError,
  Pill,
  RatingBadge,
  TimezoneNotice,
  classNames,
  modeLabel,
  modeTone,
  money,
} from "./bookingUi";

const SLOT_PAGE_SIZE = 50;

function initialMode(service) {
  const modes = service?.supported_modes || [];
  return modes.length === 1 ? modes[0] : "";
}

function initialLocation(service, mode) {
  const locations = service?.bookable_locations || [];
  return mode === "in_clinic" && locations.length === 1 ? locations[0].practice_location_id : "";
}

export default function ServiceBookingPanel({ service, displayTimezone, dateFrom: initialFrom, dateTo: initialTo, onClose, onBooked, onDateOfBirthSaved }) {
  const titleId = `booking-panel-title-${service.service_version_id}`;
  const dialogRef = useRef(null);
  const [mode, setMode] = useState(() => initialMode(service));
  const [locationId, setLocationId] = useState(() => initialLocation(service, initialMode(service)));
  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [page, setPage] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [stage, setStage] = useState("select");
  const slots = useSequencedRequest(null);
  const { run: runSlots, cancel: cancelSlots, setData: setSlotData } = slots;
  // Task 2: the live hold (id + a release closure holding the in-memory token),
  // a recovered hold to continue with, and a pending "leave while holding" action.
  const holdRef = useRef(null);
  const [recovered, setRecovered] = useState(null);
  const [operation, setOperation] = useState(null);
  const operationRef = useRef(null);
  operationRef.current = operation;
  const [existingHold, setExistingHold] = useState(null);
  const [recoveryError, setRecoveryError] = useState("");
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const [pendingExit, setPendingExit] = useState(null);
  const [exitBusy, setExitBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [askDateOfBirth, setAskDateOfBirth] = useState(false);
  const continuingRef = useRef(false);

  const eligibility = service.eligibility || { can_proceed: true };
  const locations = service.bookable_locations || [];
  const supportedModes = service.supported_modes || [];
  const rangeProblem = dateRangeProblem(dateFrom, dateTo);
  const needsLocation = mode === "in_clinic";
  const readyForSlots = eligibility.can_proceed !== false && !!mode && (!needsLocation || !!locationId) && !rangeProblem;

  const loadSlots = useCallback(() => {
    if (!readyForSlots) {
      cancelSlots();
      setSlotData(null);
      return;
    }
    runSlots(({ signal }) =>
      patientDiscoveryApi.slots(
        {
          service_id: service.service_version_id,
          consultation_mode: mode,
          practice_location_id: needsLocation ? locationId : undefined,
          date_from: dateFrom,
          date_to: dateTo,
          display_timezone: displayTimezone,
          page,
          page_size: SLOT_PAGE_SIZE,
        },
        { signal }
      )
    );
  }, [readyForSlots, cancelSlots, setSlotData, runSlots, service.service_version_id, mode, needsLocation, locationId, dateFrom, dateTo, displayTimezone, page]);

  useEffect(() => {
    if (stage === "select") loadSlots();
  }, [loadSlots, stage]);

  useEffect(() => {
    let active = true;
    patientBookingApi
      .currentHold()
      .then((data) => {
        if (active && data?.hold) setExistingHold(data.hold);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const onHoldChange = useCallback((value) => {
    holdRef.current = value;
  }, []);

  // Leaving while a hold is live asks first, then releases it.
  const requestExit = useCallback(
    (action) => {
      if (holdRef.current) setPendingExit(() => action);
      else action();
    },
    []
  );

  async function confirmExit() {
    const action = pendingExit;
    setExitBusy(true);
    try {
      await holdRef.current?.release();
    } catch {
      // The hold will expire on its own; leaving must not be blocked.
    } finally {
      holdRef.current = null;
      setExitBusy(false);
      setPendingExit(null);
      action?.();
    }
  }

  // A hold request still in flight when the patient leaves the confirm step is
  // released once it answers, so an abandoned click never keeps a time.
  const abandonPendingOperation = useCallback(() => {
    const pending = operationRef.current;
    if (!pending || pending.state !== "pending" || holdRef.current) return;
    pending.promise
      .then((data) => (data?.hold_token ? patientBookingApi.releaseHold(data.hold_id, data.hold_token) : null))
      .catch(() => {});
  }, []);

  const closePanel = useCallback(() => {
    abandonPendingOperation();
    onClose?.();
  }, [abandonPendingOperation, onClose]);

  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !pendingExit) requestExit(closePanel);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [closePanel, requestExit, pendingExit]);

  function backToSelection({ refresh = false, message = "" } = {}) {
    abandonPendingOperation();
    // Re-entry guard: one hold per Continue until the patient is back here.
    continuingRef.current = false;
    setStage("select");
    setSelectedSlot(null);
    setRecovered(null);
    setOperation(null);
    setNotice(message);
    if (refresh) setSlotData(null);
  }

  function handleBack(options = {}) {
    if (options.abandon && holdRef.current) {
      requestExit(() => backToSelection({ refresh: true }));
      return;
    }
    holdRef.current = null;
    backToSelection(options);
  }

  const canContinue = !!selectedSlot && !!mode && (!needsLocation || !!locationId) && eligibility.can_proceed !== false;

  function continueToHold() {
    if (!canContinue || continuingRef.current || stage !== "select") return;
    continuingRef.current = true;
    setNotice("");
    // The request starts NOW, before the confirm step renders.
    setOperation(beginHold(holdRequestFor(service, selectedSlot, displayTimezone)));
    setStage("confirm");
  }

  async function continueExistingHold() {
    if (!existingHold || recoveryBusy) return;
    setRecoveryBusy(true);
    setRecoveryError("");
    try {
      const recovery = beginRecovery(existingHold.hold_id, {
        serviceVersionId: service.service_version_id,
        displayTimezone,
      });
      const data = await recovery.promise;
      setExistingHold(null);
      setSelectedSlot({ ...data.slot });
      setRecovered({ data, requestedAt: recovery.requestedAt });
      setStage("confirm");
    } catch (err) {
      setRecoveryError(
        apiErrorCode(err) === "HOLD_CONTEXT_MISMATCH"
          ? "Your held time is for a different service. Release it to book this one."
          : readApiError(err, "Your held time could not be restored.")
      );
    } finally {
      setRecoveryBusy(false);
    }
  }

  async function releaseExistingHold() {
    if (!existingHold || recoveryBusy) return;
    setRecoveryBusy(true);
    setRecoveryError("");
    try {
      const data = await patientBookingApi.recoverHold(existingHold.hold_id, {});
      await patientBookingApi.releaseHold(existingHold.hold_id, data.hold_token);
      setExistingHold(null);
    } catch (err) {
      setRecoveryError(readApiError(err, "Your held time could not be released. It will expire on its own."));
    } finally {
      setRecoveryBusy(false);
    }
  }

  function chooseMode(next) {
    if (next === mode) return;
    setMode(next);
    setLocationId(next === "in_clinic" ? initialLocation(service, next) : "");
    setSelectedSlot(null);
    setPage(1);
  }

  function chooseLocation(next) {
    setLocationId(next);
    setSelectedSlot(null);
    setPage(1);
  }

  function chooseDates(from, to) {
    setDateFrom(from);
    setDateTo(to);
    setSelectedSlot(null);
    setPage(1);
  }

  const grouped = useMemo(() => {
    const groups = [];
    for (const slot of slots.data?.items || []) {
      const last = groups[groups.length - 1];
      if (last && last.date === slot.local_date) last.items.push(slot);
      else groups.push({ date: slot.local_date, items: [slot] });
    }
    return groups;
  }, [slots.data]);

  const totalPages = slots.data?.total_pages || 0;
  const doctorName = service.doctor_display_name || service.doctor_full_name || "Doctor";

  return (
    <div className="fixed inset-0 z-50 flex bg-slate-900/50">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="ml-auto flex h-full w-full max-w-5xl flex-col bg-white shadow-2xl outline-none"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border p-5 sm:p-6">
          <div className="min-w-0">
            <h2 id={titleId} className="text-xl font-semibold">{service.service_name}</h2>
            <div className="mt-1 text-sm text-muted">{doctorName} • {service.clinic_name}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Pill tone="neutral">{money(service.price, service.currency)}</Pill>
              <Pill tone="neutral">{service.duration_min} min</Pill>
              <RatingBadge rating={service.service_avg_rating} count={service.service_review_count} />
              {service.eligibility?.age_limit_text ? <Pill tone="warn">{service.eligibility.age_limit_text}</Pill> : null}
            </div>
            <TimezoneNotice timeZone={displayTimezone} className="mt-2" />
          </div>
          <button type="button" onClick={() => requestExit(closePanel)} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50">
            Close
          </button>
        </div>

        <div className="grid flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-12 lg:overflow-hidden">
          <div className="border-b border-border p-5 sm:p-6 lg:col-span-5 lg:overflow-y-auto lg:border-b-0 lg:border-r">
            <section aria-labelledby="review-heading">
              <h3 id="review-heading" className="text-sm font-semibold">About this visit</h3>
              <p className="mt-2 text-sm leading-6 text-muted">{service.full_description || service.short_description || "No additional description has been added yet."}</p>
              {service.contraindications ? (
                <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <div className="font-semibold">Please review before booking</div>
                  <div className="mt-1">{service.contraindications}</div>
                </div>
              ) : null}
            </section>

            {eligibility.can_proceed === false ? (
              <div role="status" className="mt-5 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="eligibility-guidance">
                <div className="font-semibold">{eligibility.status === "profile_incomplete" ? "Date of birth needed" : "Not available for your age"}</div>
                <p className="mt-1">{eligibility.message}</p>
                {eligibility.status === "profile_incomplete" ? (
                  <button
                    type="button"
                    onClick={() => setAskDateOfBirth(true)}
                    className="mt-3 rounded-2xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                    data-testid="add-date-of-birth"
                  >
                    Add date of birth
                  </button>
                ) : null}
              </div>
            ) : (
              <>
                {stage !== "select" ? (
                  <p className="mt-5 rounded-2xl border border-border bg-surface p-3 text-xs text-muted" data-testid="selection-locked">
                    Your selection is locked while this time is held. Choose another time to change it.
                  </p>
                ) : null}
                <fieldset className="mt-5" disabled={stage !== "select"}>
                  <legend className="text-sm font-semibold">Consultation type</legend>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Consultation type">
                    {supportedModes.map((value) => {
                      const availability = (service.mode_availability || []).find((m) => m.mode === value);
                      const active = mode === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => chooseMode(value)}
                          className={classNames("flex items-center gap-3 rounded-2xl border p-3 text-left text-sm", active ? "border-primary bg-primary/5" : "border-border bg-white hover:bg-slate-50")}
                        >
                          <Icon name={value === "video" ? "video" : "map"} className="h-5 w-5 shrink-0" />
                          <span>
                            <span className="block font-semibold">{modeLabel(value)}</span>
                            <span className="block text-xs text-muted">
                              {value === "video" ? "No location needed" : "Visit a practice location"}
                              {availability ? ` · ${availability.availability_count} in search dates` : ""}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                {needsLocation ? (
                  <div className="mt-5">
                    <label htmlFor="booking-location" className="text-sm font-semibold">Location</label>
                    <select
                      id="booking-location"
                      disabled={stage !== "select"}
                      value={locationId}
                      onChange={(e) => chooseLocation(e.target.value)}
                      className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">Choose a location</option>
                      {locations.map((loc) => (
                        <option key={loc.practice_location_id} value={loc.practice_location_id}>
                          {[loc.location_name, loc.city].filter(Boolean).join(" · ")}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="booking-date-from" className="text-sm font-semibold">From</label>
                    <input id="booking-date-from" disabled={stage !== "select"} type="date" value={dateFrom} onChange={(e) => chooseDates(e.target.value, dateTo)} className="mt-2 w-full rounded-2xl border border-border bg-white px-3 py-2.5 text-sm" />
                  </div>
                  <div>
                    <label htmlFor="booking-date-to" className="text-sm font-semibold">To</label>
                    <input id="booking-date-to" disabled={stage !== "select"} type="date" value={dateTo} min={dateFrom} onChange={(e) => chooseDates(dateFrom, e.target.value)} className="mt-2 w-full rounded-2xl border border-border bg-white px-3 py-2.5 text-sm" />
                  </div>
                </div>
                {rangeProblem ? <p role="alert" className="mt-2 text-sm text-rose-700">{rangeProblem}</p> : null}
              </>
            )}
          </div>

          <div className="p-5 sm:p-6 lg:col-span-7 lg:overflow-y-auto">
            {stage === "confirm" && selectedSlot ? (
              <HoldAndBook
                service={service}
                slot={selectedSlot}
                displayTimezone={displayTimezone}
                operation={recovered ? null : operation}
                recovered={recovered}
                onHoldChange={onHoldChange}
                onBack={handleBack}
                onBooked={onBooked}
              />
            ) : (
              <section aria-labelledby="slots-heading">
                <div className="flex items-center justify-between gap-3">
                  <h3 id="slots-heading" className="text-sm font-semibold">Available times</h3>
                  {slots.loading && slots.data ? <span className="text-xs text-muted" aria-live="polite">Updating…</span> : null}
                </div>

                {notice ? (
                  <div role="alert" className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="slot-notice">
                    {notice} Availability has been refreshed.
                  </div>
                ) : null}

                {existingHold ? (
                  <div className="mt-3 rounded-2xl border border-sky-300 bg-sky-50 p-4 text-sm" data-testid="existing-hold">
                    <p className="font-semibold">You have a time on hold until {formatTimeInZone(existingHold.expires_at, displayTimezone)}.</p>
                    <p className="mt-1">{formatLocalDate(existingHold.start_at?.slice(0, 10))} at {formatTimeInZone(existingHold.start_at, displayTimezone)}</p>
                    {recoveryError ? <p role="alert" className="mt-2 text-rose-700">{recoveryError}</p> : null}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button type="button" onClick={continueExistingHold} disabled={recoveryBusy} className="rounded-2xl bg-sky-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Continue with held time</button>
                      <button type="button" onClick={releaseExistingHold} disabled={recoveryBusy} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-60">Release it</button>
                    </div>
                  </div>
                ) : null}

                {eligibility.can_proceed === false ? (
                  <p className="mt-4 rounded-2xl border border-border bg-surface p-5 text-sm text-muted">Times are not shown for this service.</p>
                ) : !mode ? (
                  <p className="mt-4 rounded-2xl border border-border bg-surface p-5 text-sm text-muted">Choose a consultation type to see times.</p>
                ) : needsLocation && !locationId ? (
                  <p className="mt-4 rounded-2xl border border-border bg-surface p-5 text-sm text-muted">Choose a location to see times.</p>
                ) : rangeProblem ? null : slots.error ? (
                  <div className="mt-4">
                    <InlineError message={readApiError(slots.error, "Failed to load available times.")} onRetry={loadSlots} />
                  </div>
                ) : slots.loading && !slots.data ? (
                  <p className="mt-4 rounded-2xl border border-border bg-surface p-5 text-sm text-muted" role="status">Loading times…</p>
                ) : grouped.length === 0 ? (
                  <p className="mt-4 rounded-2xl border border-border bg-surface p-5 text-sm text-muted" data-testid="slots-empty">No times are available for these dates. Try other dates.</p>
                ) : (
                  <div className="mt-4 space-y-5" role="radiogroup" aria-labelledby="slots-heading">
                    {grouped.map((group) => (
                      <div key={group.date}>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted">{formatLocalDate(group.date)}</div>
                        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {group.items.map((slot) => {
                            const active = selectedSlot?.slot_inventory_id === slot.slot_inventory_id;
                            return (
                              <button
                                key={slot.slot_inventory_id}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => setSelectedSlot(slot)}
                                className={classNames(
                                  "rounded-2xl border px-3 py-2.5 text-left text-sm transition",
                                  active ? "border-primary bg-primary/10 font-semibold shadow-sm" : "border-border bg-white hover:bg-slate-50"
                                )}
                              >
                                <span className="flex items-center justify-between gap-1">
                                  {formatTimeInZone(slot.start_at, displayTimezone)}
                                  {active ? <Icon name="check" className="h-4 w-4 text-primary" /> : null}
                                </span>
                                {slot.location_name ? <span className="block truncate text-xs text-muted">{slot.location_name}</span> : null}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {totalPages > 1 && stage === "select" ? (
                  <div className="mt-5 flex items-center justify-between">
                    <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Earlier</button>
                    <span className="text-sm text-muted">Page {page} of {totalPages}</span>
                    <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Later</button>
                  </div>
                ) : null}

                {selectedSlot ? (
                  <div className="sticky bottom-0 mt-5 flex flex-col gap-2 rounded-2xl border border-primary/30 bg-white p-4 shadow-soft sm:flex-row sm:items-center sm:justify-between" data-testid="selected-slot-summary">
                    <div className="text-sm">
                      <span className="font-semibold">{formatLocalDate(selectedSlot.local_date)}</span>{" "}
                      {formatTimeInZone(selectedSlot.start_at, displayTimezone)} · <Pill tone={modeTone(selectedSlot.consultation_mode)}>{modeLabel(selectedSlot.consultation_mode)}</Pill>
                    </div>
                    <button type="button" onClick={continueToHold} disabled={!canContinue} className="btn-primary-gradient rounded-2xl px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
                      Continue
                    </button>
                  </div>
                ) : null}
              </section>
            )}
          </div>
        </div>
      </div>
      <DateOfBirthDialog
        open={askDateOfBirth}
        ageLimitText={eligibility.age_limit_text}
        onCancel={() => setAskDateOfBirth(false)}
        onSaved={async () => {
          setAskDateOfBirth(false);
          setNotice("Date of birth saved. Checking this service again…");
          // The server decides eligibility, so the service is re-read rather
          // than assumed bookable: the patient may still be outside the limit.
          await onDateOfBirthSaved?.();
        }}
      />

      <ConfirmDialog
        open={!!pendingExit}
        title="Release your held time?"
        description="This time is being held for you. If you leave, it will be released so others can book it."
        confirmLabel="Release and leave"
        cancelLabel="Keep my hold"
        busy={exitBusy}
        onConfirm={confirmExit}
        onCancel={() => setPendingExit(null)}
      />
    </div>
  );
}
